// PhoneSettings.tsx — the Phone card's own controls (Oct 27 2026): where Twilio sends calls, your own numbers
// (the callers whose requests your EmCog takes), approved callers (friends and family it chats with), when your
// EmCog may call you, calls waiting for your yes, calls you asked for, and the recent call log. The Twilio keys themselves are on the credential row above it (encrypted vault).
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export type CallPermission = 'never' | 'ask' | 'matters';

export interface PhoneInfo {
  ready: boolean;
  configured?: boolean;
  emcogNumber?: string | null;
  trustedNumbers?: string[];
  /** People you allowed to call your EmCog, to chat (Oct 27 2026): the name, the number (E.164 and as shown), who they are in its memory. */
  approvedCallers?: Array<{ name: string; number: string; shown: string; knownAs: string | null }>;
  permission?: CallPermission;
  /** An unverified caller ID from your number is asked for the PIN on the keypad (false: the PIN isn't all digits). */
  keypadPin?: boolean;
  webhookUrl?: string | null;
  httpsAddress?: boolean;
  busy?: boolean;
  scheduled?: Array<{ id: string; at: number; when: string; purpose: string | null }>;
  pending?: Array<{ id: string; to: string; name: string | null; purpose: string | null; createdAt: number }>;
  recentCalls?: Array<{ at: number; direction: 'inbound' | 'outbound'; party: 'person' | 'other' | 'approved' | 'stranger'; number: string; name?: string; purpose?: string; outcome: string; seconds?: number }>;
}

const PERMISSIONS: Array<{ id: CallPermission; label: string; detail: string }> = [
  { id: 'never', label: 'Never call me', detail: 'Your EmCog never calls you, even if asked. You can still call them.' },
  { id: 'ask', label: 'Only when I ask', detail: '"Call me at 3", "call me back in ten minutes", "give me a call now".' },
  { id: 'matters', label: 'Also when it really matters', detail: 'Today that means one thing: a reminder you leave unanswered through all its nudges gets one call (never during Do Not Disturb).' }
];

const outcomeWords = (c: NonNullable<PhoneInfo['recentCalls']>[number]): string => {
  const who = c.party === 'person' ? 'you' : (c.name || c.number);
  const length = c.seconds ? ` · ${Math.floor(c.seconds / 60)}m ${c.seconds % 60}s` : '';
  if (c.outcome === 'talked') return `${c.direction === 'inbound' ? `${c.party === 'person' ? 'You' : who} called` : `Called ${who}`}${length}`;
  if (c.outcome === 'left a message') return `${c.number} left a message${length}`;
  if (c.outcome === 'no message') return `${c.number} called · no message`;
  return `Called ${who} · ${c.outcome.replace(/-/g, ' ')}`;
};

const PhoneSettings: React.FC<{ info: PhoneInfo | null; onChanged: () => void }> = ({ info, onChanged }) => {
  const { token } = useAuth();
  const [numbers, setNumbers] = useState('');
  const [permission, setPermission] = useState<CallPermission>('ask');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Approved callers (Oct 27 2026): the list as edited here, and the row being added.
  const [callers, setCallers] = useState<Array<{ name: string; number: string }>>([]);
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');

  // The form follows the server whenever it reports something new (another tab, or the first load).
  const serverNumbers = (info?.trustedNumbers || []).join(', ');
  const serverPermission = info?.permission || 'ask';
  const serverCallers = JSON.stringify((info?.approvedCallers || []).map(c => ({ name: c.name, number: c.number })));
  useEffect(() => { setNumbers(serverNumbers); }, [serverNumbers]);
  useEffect(() => { setPermission(serverPermission); }, [serverPermission]);
  useEffect(() => { setCallers(JSON.parse(serverCallers)); }, [serverCallers]);

  const headers = (json = false): Record<string, string> => ({ ...(json ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) });

  const addCaller = () => {
    const name = newName.trim();
    const number = newNumber.trim();
    if (!name || !number) { setProblem('An approved caller needs a name and a number.'); return; }
    setCallers(list => [...list, { name, number }]);
    setNewName(''); setNewNumber(''); setSaved(false); setProblem(null);
  };
  const removeCaller = (i: number) => { setCallers(list => list.filter((_, j) => j !== i)); setSaved(false); };
  const renameCaller = (i: number, name: string) => { setCallers(list => list.map((c, j) => (j === i ? { ...c, name } : c))); setSaved(false); };

  const save = async () => {
    setSaving(true); setProblem(null); setSaved(false);
    try {
      // A name and number typed but not yet added are saved with the rest, rather than lost.
      const pending = newName.trim() && newNumber.trim() ? [{ name: newName.trim(), number: newNumber.trim() }] : [];
      const r = await fetch('/api/phone/settings', { method: 'PUT', headers: headers(true), body: JSON.stringify({ trustedNumbers: numbers, approvedCallers: [...callers, ...pending], permission }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setProblem(d.error || `Couldn't save (${r.status}).`); return; }
      if (pending.length) { setNewName(''); setNewNumber(''); }
      setSaved(true);
      onChanged();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const act = async (url: string) => {
    setProblem(null);
    try {
      const r = await fetch(url, { method: 'POST', headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) setProblem(d.text || d.error || `That didn't work (${r.status}).`);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    }
    onChanged();
  };

  if (!info) return null;
  if (!info.ready) return <p className="connection-note">The phone line isn't running on this EmCog (its voice isn't available).</p>;

  return (
    <div className="phone-settings">
      {info.webhookUrl
        ? <p className="connection-note">In Twilio, set your EmCog's number → Voice Configuration → "A call comes in" to Webhook, HTTP POST: <code style={{ userSelect: 'all' }}>{info.webhookUrl}</code></p>
        : <p className="connection-note">Set the public address first (an https address): Twilio sends calls to it.</p>}
      {info.webhookUrl && info.httpsAddress === false && <p className="connection-note">The public address isn't https. Twilio can only stream a call's voice to an https (wss) address, so calls go to messages until it is.</p>}
      {info.configured && info.emcogNumber && <p className="connection-note">Your EmCog's number: <b>{info.emcogNumber}</b>{info.busy ? ' · on a call right now' : ''}</p>}

      <label style={{ display: 'block', fontSize: 13, margin: '10px 0 4px' }}>Your numbers: you — your EmCog takes requests only from these</label>
      <input
        type="text"
        value={numbers}
        onChange={e => { setNumbers(e.target.value); setSaved(false); }}
        placeholder="+12175550134"
        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px' }}
      />
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>With the country code. Several? Separate them with commas. The first one is the number your EmCog calls.</div>
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
        {info.keypadPin === false
          ? 'When your phone company can\'t fully verify your caller ID, the call goes to a message: your sign-in PIN isn\'t all digits, so it can\'t be entered on the keypad.'
          : 'When your phone company can\'t fully verify your caller ID, your EmCog asks you to enter your PIN on the keypad (the PIN you sign in with), then #. A wrong PIN goes to a message.'}
      </div>

      <div style={{ fontSize: 13, margin: '12px 0 4px' }}>Approved callers: friends and family your EmCog will chat with — they can't ask your EmCog to do things</div>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>
        Your EmCog greets them by name, remembers them, and keeps your private life to itself. Only a fully verified caller ID gets through; anything less goes to a message (the keypad PIN is yours alone). You're told who called and when. "Call Kaylee" works too — it still waits for your yes.
      </div>
      {callers.map((c, i) => (
        <div key={`${c.number}_${i}`} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0', flexWrap: 'wrap' }}>
          <input type="text" value={c.name} onChange={e => renameCaller(i, e.target.value)} aria-label="Name" style={{ flex: '1 1 120px', minWidth: 0, padding: '4px 6px' }} />
          {(() => {
            const onServer = (info.approvedCallers || []).find(a => a.number === c.number);
            const known = onServer?.knownAs && onServer.knownAs.toLowerCase() !== c.name.trim().toLowerCase() ? ` · remembered as ${onServer.knownAs}` : '';
            return <span style={{ flex: '1 1 140px', fontSize: 13 }}>{onServer?.shown || c.number}{known}</span>;
          })()}
          <button className="download-btn" onClick={() => removeCaller(i)}>Remove</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0', flexWrap: 'wrap' }}>
        <input type="text" value={newName} onChange={e => { setNewName(e.target.value); setSaved(false); }} placeholder="Name" aria-label="New caller's name" style={{ flex: '1 1 120px', minWidth: 0, padding: '4px 6px' }} />
        <input type="text" value={newNumber} onChange={e => { setNewNumber(e.target.value); setSaved(false); }} placeholder="+12175550142" aria-label="New caller's number" style={{ flex: '1 1 140px', minWidth: 0, padding: '4px 6px' }} />
        <button className="download-btn" onClick={addCaller}>Add</button>
      </div>
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>Changes to the list are kept when you click Save. Removing someone only takes away the calling — your EmCog still remembers them.</div>

      <div style={{ fontSize: 13, margin: '12px 0 4px' }}>When may your EmCog call you?</div>
      {PERMISSIONS.map(p => (
        <label key={p.id} style={{ display: 'block', margin: '4px 0', cursor: 'pointer' }}>
          <input type="radio" name="call-permission" checked={permission === p.id} onChange={() => { setPermission(p.id); setSaved(false); }} />{' '}
          <b>{p.label}</b> <span style={{ fontSize: 12, opacity: 0.75 }}>— {p.detail}</span>
        </label>
      ))}
      <p style={{ marginTop: 10 }}>
        <button className="download-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        {saved && <span style={{ marginLeft: 8, fontSize: 13 }}>Saved.</span>}
      </p>
      {problem && <p className="connection-note">{problem}</p>}

      {(info.pending || []).length > 0 && (
        <>
          <div style={{ fontSize: 13, margin: '12px 0 4px' }}>Waiting for your yes (here, or type "approve" and the id in chat — never on a call)</div>
          {(info.pending || []).map(p => (
            <p key={p.id} className="connection-note">
              Call <b>{p.name ? `${p.name} (${p.to})` : p.to}</b>{p.purpose ? ` — ${p.purpose}` : ''}{' '}
              <button className="download-btn" onClick={() => act(`/api/phone/pending/${encodeURIComponent(p.id)}/approve`)}>Approve</button>{' '}
              <button className="download-btn" onClick={() => act(`/api/phone/pending/${encodeURIComponent(p.id)}/deny`)}>Deny</button>
            </p>
          ))}
        </>
      )}

      {(info.scheduled || []).length > 0 && (
        <>
          <div style={{ fontSize: 13, margin: '12px 0 4px' }}>Calls you asked for</div>
          {(info.scheduled || []).map(c => (
            <p key={c.id} className="connection-note">
              {c.when}{c.purpose ? ` — ${c.purpose}` : ''}{' '}
              <button className="download-btn" onClick={() => act(`/api/phone/scheduled/${encodeURIComponent(c.id)}/cancel`)}>Call off</button>
            </p>
          ))}
        </>
      )}

      {(info.recentCalls || []).length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Recent calls</summary>
          {(info.recentCalls || []).map((c, i) => (
            <p key={`${c.at}_${i}`} className="connection-note">{new Date(c.at).toLocaleString()} · {outcomeWords(c)}</p>
          ))}
        </details>
      )}
    </div>
  );
};

export default PhoneSettings;
